import React from "react";
import ReactDOM from "react-dom";
import ErrorBoundary from "../error-boundary/ErrorBoundary";
import HeadingErrorBoundary from "./HeadingErrorBoundary";
import MegaMenuPanel from "./mega-menu/MegaMenuPanel";
import MegaMenuMobilePanel from "./mega-menu/MegaMenuMobilePanel";
import { navItemHasMegaMenu } from "./mega-menu/megaMenuUtils";
import SearchOverlay from "./search-overlay/SearchOverlay";
import {
  SEARCH_SUGGEST_MIN_CHARS,
  buildSuggestUrl
} from "./search-overlay/searchOverlayConstants";
import {
  fetchMockSuggestions,
  shouldUseSearchSuggestMock
} from "./search-overlay/searchOverlayMock";
import { createDebouncer } from "./search-overlay/searchOverlayUtils";
import { ChevronDownIcon, CloseIcon, MenuIcon, SearchIcon } from "./icons";
import isHomePage, { removeHomePageHeading } from "../../utilities/isHomePage";

class Header extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hamburgerOpen: false,
      searchOpen: false,
      searchTerm: "",
      autoCompleteSuggestions: false,
      activeMegaId: null,
      mobileAccordionId: null
    };

    this.headerRef = React.createRef();
    this.searchToggleRef = React.createRef();
    this.searchRequestId = 0;
    this.searchAbortController = null;
    this.debouncedFetchSuggestions = createDebouncer(250, term => {
      this.fetchSuggestions(term);
    });
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleDocumentMouseDown);
    document.addEventListener("keydown", this.handleDocumentKeyDown);
  }

  componentDidUpdate(prevProps, prevState) {
    const wasLocked = prevState.hamburgerOpen || prevState.searchOpen;
    const isLocked = this.state.hamburgerOpen || this.state.searchOpen;
    if (wasLocked !== isLocked) {
      document.body.classList.toggle("bv-mega-header--scroll-lock", isLocked);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleDocumentMouseDown);
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
    document.body.classList.remove("bv-mega-header--scroll-lock");
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }
    this.debouncedFetchSuggestions.cancel();
  }

  getMainNavItems() {
    return this.props.navData?.navigation || [];
  }

  handleDocumentMouseDown = event => {
    if (!this.headerRef.current?.contains(event.target)) {
      this.setState({ activeMegaId: null });
    }
  };

  handleDocumentKeyDown = event => {
    if (event.key === "Escape") {
      if (this.state.searchOpen) {
        this.closeSearch();
        return;
      }
      if (this.state.activeMegaId) {
        this.setState({ activeMegaId: null });
        return;
      }
      if (this.state.hamburgerOpen) {
        this.closeMobileMenu();
      }
    }
  };

  toggleSearch = () => {
    const willClose = this.state.searchOpen;
    if (willClose) {
      this.resetSearchState();
    }
    this.setState(prevState => {
      const searchOpen = !prevState.searchOpen;
      return {
        searchOpen,
        autoCompleteSuggestions: false,
        searchTerm: searchOpen ? prevState.searchTerm : "",
        hamburgerOpen: false,
        activeMegaId: null,
        mobileAccordionId: null
      };
    });
  };

  resetSearchState = () => {
    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }
    this.searchRequestId += 1;
    this.debouncedFetchSuggestions.cancel();
  };

  closeSearch = () => {
    this.resetSearchState();
    this.setState({
      searchOpen: false,
      autoCompleteSuggestions: false,
      searchTerm: ""
    });
    if (this.searchToggleRef.current) {
      this.searchToggleRef.current.focus();
    }
  };

  toggleMobileMenu = () => {
    this.setState(prevState => {
      const hamburgerOpen = !prevState.hamburgerOpen;
      return {
        hamburgerOpen,
        searchOpen: false,
        activeMegaId: null,
        mobileAccordionId: hamburgerOpen ? prevState.mobileAccordionId : null
      };
    });
  };

  closeMobileMenu = () => {
    this.setState({
      hamburgerOpen: false,
      mobileAccordionId: null
    });
  };

  toggleMegaMenu = assetID => {
    this.setState(prevState => ({
      activeMegaId: prevState.activeMegaId === assetID ? null : assetID,
      searchOpen: false,
      hamburgerOpen: false
    }));
  };

  toggleMobileAccordion = assetID => {
    this.setState(prevState => ({
      mobileAccordionId:
        prevState.mobileAccordionId === assetID ? null : assetID
    }));
  };

  updateSearchTerm = event => {
    const searchTerm = event.target.value;
    this.setState({ searchTerm, autoCompleteSuggestions: false });
    if (searchTerm.length >= SEARCH_SUGGEST_MIN_CHARS) {
      this.debouncedFetchSuggestions(searchTerm);
    } else {
      this.resetSearchState();
    }
  };

  fetchSuggestions = term => {
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }
    this.searchAbortController = new AbortController();
    const requestId = ++this.searchRequestId;
    const signal = this.searchAbortController.signal;

    const applySuggestions = data => {
      if (requestId !== this.searchRequestId) {
        return;
      }
      this.setState({
        autoCompleteSuggestions: data.length ? data : false
      });
    };

    const handleError = error => {
      if (error.name === "AbortError") {
        return;
      }
      if (requestId !== this.searchRequestId) {
        return;
      }
      this.setState({ autoCompleteSuggestions: false });
    };

    if (shouldUseSearchSuggestMock()) {
      fetchMockSuggestions(term, { signal })
        .then(applySuggestions)
        .catch(handleError);
      return;
    }

    const endpoint = buildSuggestUrl(term);
    fetch(endpoint, { signal })
      .then(response => response.json())
      .then(applySuggestions)
      .catch(handleError);
  };

  shouldRenderHeading() {
    if (isHomePage()) {
      return false;
    }
    return shouldShowPageHeading(this.props.headingData);
  }

  render() {
    const navItems = this.getMainNavItems();
    const {
      activeMegaId,
      hamburgerOpen,
      mobileAccordionId,
      searchOpen
    } = this.state;

    const logoStructuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      url: "https://business.vic.gov.au",
      logo: "https://business.vic.gov.au" + require("../../images/bv-logo.svg")
    };

    return (
      <header
        className={`bv-mega-header ${
          activeMegaId ? "bv-mega-header--mega-open" : ""
        }`}
        ref={this.headerRef}
      >
        <div className="bv-mega-header__bar">
          <div className="bv-mega-header__logo-wrap d-print-none">
            <a
              href="https://business.vic.gov.au"
              className="bv-mega-header__logo-link"
            >
              <img
                src={require("../../images/bv-logo.svg")}
                className="bv-mega-header__logo"
                alt="Business Victoria logo"
              />
            </a>
          </div>

          <nav className="bv-mega-header__nav" aria-label="Primary">
            <ul className="bv-mega-header__nav-list">
              {navItems.map(item => {
                const hasMega = navItemHasMegaMenu(item);
                const isActive = activeMegaId === item.assetID;
                if (hasMega) {
                  return (
                    <li
                      key={item.assetID}
                      className={`bv-mega-header__nav-item ${
                        isActive ? "bv-mega-header__nav-item--active" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="bv-mega-header__nav-trigger"
                        aria-expanded={isActive}
                        aria-controls={`bv-mega-panel-${item.assetID}`}
                        onClick={() => this.toggleMegaMenu(item.assetID)}
                      >
                        <span>{item.pageName}</span>
                        <ChevronDownIcon
                          className="bv-mega-header__nav-chevron"
                          size={20}
                        />
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={item.assetID} className="bv-mega-header__nav-item">
                    <a className="bv-mega-header__nav-link" href={item.pageURL}>
                      {item.pageName}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="bv-mega-header__actions">
            <button
              type="button"
              ref={this.searchToggleRef}
              className={`bv-mega-header__icon-button bv-mega-header__icon-button--search ${
                searchOpen ? "bv-mega-header__icon-button--active" : ""
              }`}
              aria-label={searchOpen ? "Close search" : "Open search"}
              aria-expanded={searchOpen}
              aria-controls="bv-global-search"
              onClick={this.toggleSearch}
            >
              {/* Both icons render; CSS decides which shows. Below lg the
                  overlay is a drawer under the bar, so the toggle becomes the
                  close X. At lg+ the overlay is a modal with its own Close
                  button, so the bar keeps the magnifier. */}
              <SearchIcon className="bv-mega-header__icon--search" />
              <CloseIcon className="bv-mega-header__icon--close" />
            </button>
            <button
              type="button"
              className={`bv-mega-header__icon-button bv-mega-header__icon-button--menu ${
                hamburgerOpen ? "bv-mega-header__icon-button--active" : ""
              }`}
              aria-expanded={hamburgerOpen}
              aria-controls="bv-mega-mobile-drawer"
              aria-label={hamburgerOpen ? "Close menu" : "Open menu"}
              onClick={this.toggleMobileMenu}
            >
              {hamburgerOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>

        {activeMegaId && (
          <button
            type="button"
            className="bv-mega-header__backdrop"
            aria-label="Close menu"
            onClick={() => this.setState({ activeMegaId: null })}
          />
        )}

        {activeMegaId && (
          <div className="bv-mega-header__mega-host">
            {navItems
              .filter(
                item =>
                  item.assetID === activeMegaId && navItemHasMegaMenu(item)
              )
              .map(item => (
                <MegaMenuPanel
                  key={item.assetID}
                  navItem={item}
                  panelId={`bv-mega-panel-${item.assetID}`}
                  hidden={false}
                />
              ))}
          </div>
        )}

        {!hamburgerOpen ? null : (
          <div
            className="bv-mega-header__mobile-drawer"
            id="bv-mega-mobile-drawer"
          >
            <ul className="bv-mega-header__mobile-list">
              {navItems.map(item => {
                const hasMega = navItemHasMegaMenu(item);
                const expanded = mobileAccordionId === item.assetID;
                if (hasMega) {
                  return (
                    <li
                      key={item.assetID}
                      className={`bv-mega-header__mobile-item ${
                        expanded ? "bv-mega-header__mobile-item--active" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="bv-mega-header__mobile-trigger"
                        aria-expanded={expanded}
                        aria-controls={`bv-mega-mobile-panel-${item.assetID}`}
                        onClick={() => this.toggleMobileAccordion(item.assetID)}
                      >
                        <span>{item.pageName}</span>
                        <ChevronDownIcon
                          className="bv-mega-header__mobile-chevron"
                          size={20}
                        />
                      </button>
                      {!expanded ? null : (
                        <div
                          id={`bv-mega-mobile-panel-${item.assetID}`}
                          className="bv-mega-header__mobile-panel"
                        >
                          <MegaMenuMobilePanel navItem={item} />
                        </div>
                      )}
                    </li>
                  );
                }
                return (
                  <li
                    key={item.assetID}
                    className="bv-mega-header__mobile-item"
                  >
                    <a
                      className="bv-mega-header__mobile-link"
                      href={item.pageURL}
                    >
                      {item.pageName}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <script type="application/ld+json">
          {JSON.stringify(logoStructuredData)}
        </script>

        {this.shouldRenderHeading() && (
          <HeadingErrorBoundary
            headingData={this.props.headingData}
            linkBoxData={this.props.linkBoxData}
            breadcrumbsData={this.props.breadcrumbsData}
            secondaryNavMobData={this.props.secondaryNavMobData}
          />
        )}

        <SearchOverlay
          open={searchOpen}
          onClose={this.closeSearch}
          searchTerm={this.state.searchTerm}
          onSearchTermChange={this.updateSearchTerm}
          suggestions={this.state.autoCompleteSuggestions}
        />
      </header>
    );
  }
}

function shouldShowPageHeading(data) {
  if (!data || typeof data !== "object") {
    return false;
  }
  if (data.type === "image" || data.type === "linkbox") {
    return true;
  }
  // Text-only heroes (type "none") still show when the page has a heading.
  return Boolean(data.heading);
}

function readCmsGlobal(name) {
  return typeof window !== "undefined" ? window[name] : undefined;
}

function getHeaderCmsProps() {
  return {
    headingData: readCmsGlobal("headingData") || {
      type: "none",
      heading: ""
    },
    breadcrumbsData: readCmsGlobal("breadcrumbsDataCMS") || {
      displayBreadcrumbs: false,
      links: []
    },
    secondaryNavMobData: readCmsGlobal("secondaryNavDataCMS") || {
      active: false
    }
  };
}

function ensureHeadingMount() {
  if (isHomePage()) {
    return;
  }

  const header = document.querySelector("#header-react header");
  if (!header || header.querySelector("section#heading")) {
    return;
  }

  const cms = getHeaderCmsProps();
  if (!shouldShowPageHeading(cms.headingData)) {
    return;
  }

  const slot =
    header.querySelector(".header-heading-mount") ||
    (() => {
      const node = document.createElement("div");
      node.className = "header-heading-mount";
      header.appendChild(node);
      return node;
    })();

  ReactDOM.render(
    <ErrorBoundary>
      <HeadingErrorBoundary
        headingData={cms.headingData}
        breadcrumbsData={cms.breadcrumbsData}
        secondaryNavMobData={cms.secondaryNavMobData}
      />
    </ErrorBoundary>,
    slot
  );
}

function mountHeaderReact() {
  const mountNode = document.getElementById("header-react");
  if (!mountNode) {
    return;
  }

  const navData = readCmsGlobal("navDataCMS");
  if (!navData || !navData.navigation) {
    return;
  }

  const cms = getHeaderCmsProps();

  ReactDOM.render(
    <ErrorBoundary>
      <Header
        navData={navData}
        headingData={cms.headingData}
        breadcrumbsData={cms.breadcrumbsData}
        secondaryNavMobData={cms.secondaryNavMobData}
      />
    </ErrorBoundary>,
    mountNode
  );

  if (!isHomePage()) {
    ensureHeadingMount();
  } else {
    removeHomePageHeading();
  }
}

function scheduleHeaderMount() {
  const run = () => {
    mountHeaderReact();
    if (isHomePage()) {
      removeHomePageHeading();
    } else {
      ensureHeadingMount();
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  window.addEventListener("load", () => {
    if (isHomePage()) {
      removeHomePageHeading();
      return;
    }
    ensureHeadingMount();
    setTimeout(ensureHeadingMount, 0);
  });
}

if (document.querySelector("#header-react")) {
  scheduleHeaderMount();
}

export default Header;
